import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldAlert, RefreshCw, Ban, CheckCircle, Pause, Play,
  EyeOff, Eye, ChevronDown, ChevronUp, Loader2, X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STATUS_META = {
  ok:           { label: "OK",           color: "#30D158", bg: "rgba(48,209,88,0.12)" },
  rate_limited: { label: "Rate Limited", color: "#FF9F0A", bg: "rgba(255,159,10,0.12)" },
  banned:       { label: "Banned",       color: "#FF453A", bg: "rgba(255,69,58,0.12)" },
  ignored:      { label: "Ignored",      color: "rgba(235,235,245,0.3)", bg: "rgba(255,255,255,0.05)" },
};

const ACTION_LABELS = {
  booking_start:      "Started Booking",
  booking_confirm:    "Confirmed Booking",
  status_lookup:      "Status Lookup",
  cancel_request:     "Cancellation",
  contact_message:    "Contact Message",
  reschedule_request: "Reschedule Request",
  admin_auth:         "Admin Sign-In",
};

function fmtTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return ts; }
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.ok;
  return (
    <span style={{
      color: meta.color, background: meta.bg,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
      textTransform: "uppercase", borderRadius: 6, padding: "3px 9px",
    }}>
      {meta.label}
    </span>
  );
}

function IpCard({ ip, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [acting, setActing] = useState(false);
  const { user } = useAuth();
  const isOwnerOrAdmin = user?.role === "owner" || user?.role === "admin";
  const meta = STATUS_META[ip.status] || STATUS_META.ok;
  const logs = [...(ip.logs || [])].reverse();

  const doAction = async (act) => {
    setActing(true);
    try {
      await api.post(`/admin/security/ips/${ip.key}/${act}`);
      const labels = {
        ban: "IP banned", unban: "IP unbanned",
        "rate-limit": "Rate limited", "unrate-limit": "Rate limit cleared",
        ignore: "Moved to Ignored", unignore: "Removed from Ignored",
      };
      toast.success(labels[act] || "Done");
      onAction();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-luxe"
      style={{ padding: "16px 20px", marginBottom: 10, borderLeft: `3px solid ${meta.color}` }}
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 600, color: meta.color }}>
              {ip.ip}
            </span>
            <StatusPill status={ip.status} />
            {ip.admin_email && (
              <span style={{
                fontSize: 10, background: "rgba(138,138,138,0.15)", color: "#8A8A8A",
                borderRadius: 4, padding: "2px 7px", fontWeight: 600,
              }}>
                {ip.admin_role || "admin"}
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: "rgba(235,235,245,0.5)", margin: 0 }}>
            {ip.request_count || 0} requests · Last seen {fmtTs(ip.last_seen)}
          </p>
          {ip.admin_email && (
            <p style={{ fontSize: 12, color: "#8A8A8A", margin: "2px 0 0" }}>
              {ip.admin_email}
            </p>
          )}
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{ background: "none", border: "none", color: "rgba(235,235,245,0.4)", cursor: "pointer", padding: 4 }}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Action buttons */}
      {isOwnerOrAdmin && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: expanded ? 14 : 0 }}>
          {ip.status !== "banned" ? (
            <ActionBtn icon={Ban} label="Ban" color="#FF453A" disabled={acting} onClick={() => doAction("ban")} />
          ) : (
            <ActionBtn icon={CheckCircle} label="Unban" color="#30D158" disabled={acting} onClick={() => doAction("unban")} />
          )}
          {ip.status === "rate_limited" ? (
            <ActionBtn icon={Play} label="Clear Limit" color="#30D158" disabled={acting} onClick={() => doAction("unrate-limit")} />
          ) : ip.status !== "banned" ? (
            <ActionBtn icon={Pause} label="Rate Limit" color="#FF9F0A" disabled={acting} onClick={() => doAction("rate-limit")} />
          ) : null}
          {ip.status !== "ignored" ? (
            <ActionBtn icon={EyeOff} label="Ignore" color="rgba(235,235,245,0.4)" disabled={acting} onClick={() => doAction("ignore")} />
          ) : (
            <ActionBtn icon={Eye} label="Unignore" color="rgba(235,235,245,0.6)" disabled={acting} onClick={() => doAction("unignore")} />
          )}
        </div>
      )}

      {/* Activity log */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: "hidden" }}
          >
            <p style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(235,235,245,0.3)", margin: "0 0 10px", fontWeight: 600 }}>
              Activity Log
            </p>
            {logs.length === 0 ? (
              <p style={{ fontSize: 13, color: "rgba(235,235,245,0.3)", textAlign: "center", padding: "16px 0" }}>No activity logged</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {logs.map((entry, i) => (
                  <div key={i} style={{ borderTop: i > 0 ? "1px solid rgba(255,255,255,0.06)" : "none", padding: "10px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#f5f5f7" }}>
                        {ACTION_LABELS[entry.action] || entry.action}
                      </span>
                      <span style={{ fontSize: 10, color: "rgba(235,235,245,0.3)", whiteSpace: "nowrap", flexShrink: 0 }}>
                        {fmtTs(entry.ts)}
                      </span>
                    </div>
                    {entry.admin_email && (
                      <p style={{ margin: "0 0 4px", fontSize: 11, color: "#8A8A8A" }}>
                        Admin: {entry.admin_email} ({entry.admin_role || ""})
                      </p>
                    )}
                    {entry.data && Object.keys(entry.data).length > 0 && (
                      <div style={{
                        fontSize: 11, fontFamily: "monospace", color: "rgba(235,235,245,0.6)",
                        background: "rgba(0,0,0,0.3)", borderRadius: 6, padding: "6px 10px",
                        wordBreak: "break-all",
                      }}>
                        {Object.entries(entry.data).map(([k, v]) => (
                          <div key={k}>
                            <span style={{ color: "rgba(235,235,245,0.35)" }}>{k}: </span>
                            {String(v)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ActionBtn({ icon: Icon, label, color, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        fontSize: 11, fontWeight: 600, color,
        background: `${color}18`, border: `1px solid ${color}33`,
        borderRadius: 6, padding: "5px 11px", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, transition: "opacity 150ms",
      }}
    >
      {disabled ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}
      {label}
    </button>
  );
}

const TABS = [
  { id: "ok",           label: "Normal" },
  { id: "ignored",      label: "Ignored" },
  { id: "rate_limited", label: "Rate Limited" },
  { id: "banned",       label: "Banned" },
];

export default function AdminSecurity() {
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ok");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/security/ips");
      setIps(data.ips || []);
    } catch (e) {
      toast.error("Failed to load security data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = ips.filter((ip) => {
    const s = ip.status || "ok";
    if (activeTab === "ok") return s === "ok";
    return s === activeTab;
  });

  const counts = {};
  TABS.forEach((t) => {
    counts[t.id] = ips.filter((ip) => {
      const s = ip.status || "ok";
      if (t.id === "ok") return s === "ok";
      return s === t.id;
    }).length;
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <p className="overline mb-1">Admin Console</p>
          <h1 className="font-serif text-3xl flex items-center gap-3">
            <ShieldAlert size={26} className="text-gold" /> Security
          </h1>
          <p className="text-white/40 text-sm mt-1">IP activity, rate limits, and bans.</p>
        </div>
        <button onClick={load} disabled={loading} className="btn-ghost" style={{ width: "auto", padding: "0 16px", height: 38 }}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "rgba(255,255,255,0.04)", padding: 4, borderRadius: 10 }}>
        {TABS.map((tab) => {
          const meta = STATUS_META[tab.id] || STATUS_META.ok;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: "8px 6px", borderRadius: 7, border: "none", cursor: "pointer",
                background: isActive ? "rgba(255,255,255,0.08)" : "transparent",
                color: isActive ? meta.color : "rgba(235,235,245,0.4)",
                fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
                transition: "all 180ms",
              }}
            >
              {tab.label}
              {counts[tab.id] > 0 && (
                <span style={{
                  marginLeft: 5, background: isActive ? `${meta.color}22` : "rgba(255,255,255,0.07)",
                  color: isActive ? meta.color : "rgba(235,235,245,0.4)",
                  borderRadius: 10, padding: "1px 6px", fontSize: 10,
                }}>
                  {counts[tab.id]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* IP list */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="card-luxe" style={{ padding: "16px 20px", height: 80, opacity: 0.4 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(235,235,245,0.3)" }}>
          <ShieldAlert size={36} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
          <p style={{ fontSize: 14 }}>No IPs in this category</p>
        </div>
      ) : (
        <div>
          {filtered.map((ip) => (
            <IpCard key={ip.key} ip={ip} onAction={load} />
          ))}
        </div>
      )}
    </div>
  );
}
